/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Table } from 'antd';
import type { FC, MouseEventHandler } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { getEmailPrefix } from '../utils';
import { useContainer } from '../StoreProvider';
import type { RewardsModalHandle } from './RewardsModal';
import type { TcProps, TcWithLoginProps } from './discount-terms';
import { getRangeTable } from './discount-terms';
import { RowsIcon, RightIcon } from './TableIcon';
import styled from './index.module.scss';
import RewardsModal from './RewardsModal';
import NextImage from '@/components/NextImage';
import { useGlobal } from '@/utils/useGlobal';
import EfModal from '@/components/UI/Modal';
import gtm from '@/utils/gtm';
import { loginUser } from '@/utils/cookie';
import type { UserInfoProps } from '@/components/MembersStore/types';
import { signupCookie } from '@/utils/cookies/signupCookie';

interface BannerProps {
    isLogin: boolean;
}

interface ButtonProps {
    text: string;
    theme: 'dark' | 'light';
    onClick?: MouseEventHandler<HTMLButtonElement>;
}

const JoinButton: FC<ButtonProps> = ({ text, theme, onClick }) => {
    return (
        <button className={`banner-button-${theme} banner-button`} onClick={onClick}>
            {text}
        </button>
    );
};

const PopupLink = ({ text, isLogin, couponName }: { text: string; isLogin: boolean; couponName?: string }) => {
    const { useRouter, useLang } = useGlobal();
    const { t } = useLang('membersstore');
    const { locale } = useRouter();
    const [show, setShow] = React.useState(false);
    const [isMounted, setIsMounted] = React.useState(false);
    const dataSource = getRangeTable(locale, isLogin, couponName);
    const { priceInfo } = useContainer();

    const tableColumns: ColumnsType<TcProps> = [
        {
            title: t('banner.table.col1'),
            dataIndex: 'range',
            key: 'range',
        },
        {
            title: t('banner.table.col2'),
            dataIndex: 'discount',
            key: 'discount',
            render: (text) => {
                return (
                    <>
                        <RowsIcon />
                        &nbsp;
                        {text}
                    </>
                );
            },
        },
    ];

    const tableColumnsWithLogin: ColumnsType<TcWithLoginProps> = [
        {
            title: t('banner.table.col1'),
            dataIndex: 'range',
            key: 'range',
        },
        {
            title: t('banner.table.col2'),
            dataIndex: 'discount',
            key: 'discount',
            render: (text) => {
                return (
                    <>
                        <RowsIcon />
                        &nbsp;
                        {text}
                    </>
                );
            },
        },
        {
            title: t('banner.table.col3'),
            dataIndex: 'checked',
            key: 'checked',
            render: (text) => {
                if (text)
                    return (
                        <>
                            <RightIcon />
                        </>
                    );
                return <></>;
            },
        },
    ];

    const BannerTitle = React.useMemo(() => {
        if (!isLogin) return t('banner.modal.title');
        return `${t('banner.modal.title1')} ${priceInfo?.currencySymbol ?? ''}${priceInfo?.subtotalPrice ?? ''}`;
    }, [isLogin, priceInfo]);

    const TableRenderer = React.useMemo(() => {
        if (isLogin) {
            return (
                <Table
                    dataSource={dataSource as TcWithLoginProps[]}
                    columns={tableColumnsWithLogin}
                    pagination={false}
                    bordered={false}
                    rowClassName={(_row, i) => `${i % 2 === 1 ? 'odd' : 'even'} ${'no-border'}`}
                />
            );
        }
        return (
            <Table
                dataSource={dataSource}
                columns={tableColumns}
                pagination={false}
                bordered={false}
                rowClassName={(_row, i) => `${i % 2 === 1 ? 'odd' : 'even'} ${'no-border'}`}
            />
        );
    }, [isLogin, dataSource]);

    React.useEffect(() => {
        setIsMounted(true);
    }, []);

    return (
        <>
            <div
                className="banner-popup-link"
                onClick={() => {
                    gtm.push({
                        event: 'uaEvent',
                        event_name: 'terms_confirm',
                        button_name: text,
                        url: 'none',
                    });
                    setShow(true);
                }}
            >
                {text}
            </div>
            {isMounted && (
                <EfModal
                    width={640}
                    visible={show}
                    open={show}
                    className={`${styled.terms_modal} ${show ? styled['animation_fade-appear-active'] : styled['animation_fade-appear-leave']}`}
                    centered={true}
                    getContainer={true}
                    onCancel={() => setShow(false)}
                    style={{ bottom: 0 }}
                >
                    <div className="terms-modal-wrapper">
                        <div className="terms-modal-title">{BannerTitle}</div>
                        {TableRenderer}
                    </div>
                </EfModal>
            )}
        </>
    );
};

const Banner: FC<BannerProps> = ({ isLogin = false }) => {
    const { isMobile, useRouter, useLang } = useGlobal();
    const { t } = useLang('membersstore');

    const router = useRouter();
    const { locale } = router;
    const rewardsRef = React.useRef<RewardsModalHandle>(null);
    const [isMounted, setIsMounted] = React.useState(false);
    const [userInfo, setUserInfo] = React.useState<UserInfoProps | null>(null);
    const { priceInfo } = useContainer();

    React.useEffect(() => {
        setIsMounted(true);
        setUserInfo(loginUser());
    }, []);

    const CouponTip = React.useMemo(() => {
        if (isLogin) {
            if (priceInfo?.receiveStatus === 1) return t('banner.coupon_tip', { discount: priceInfo?.discountName });
            if (priceInfo?.receiveStatus === 2 && priceInfo?.useStatus === 'UNUSED') return t('banner.coupon_tip1', { discount: priceInfo?.discountName });
            if (priceInfo?.receiveStatus === 2 && priceInfo?.useStatus === 'USED') return t('banner.coupon_tip2');
        }
        return '';
    }, [priceInfo, isLogin]);

    return (
        <div className={`${styled.banner}`}>
            <NextImage
                src={
                    isMobile
                        ? 'https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1713839167323/members_store_m_kv.jpg'
                        : 'https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1713839160531/members_store_pc_kv.jpg'
                }
                alt={t('banner.title')}
                aria-label={t('banner.title')}
            />
            <div className="banner_text">
                <div className="content">
                    {!isLogin ? (
                        <>
                            <h1 className="title" dangerouslySetInnerHTML={{ __html: t('banner.title') }}></h1>
                            <p className="subtitle" dangerouslySetInnerHTML={{ __html: t('banner.subtitle') }}></p>
                            <div className="tools-row">
                                <JoinButton
                                    text={t('banner.button2')}
                                    theme={'dark'}
                                    onClick={() => {
                                        gtm.push({
                                            event: 'uaEvent',
                                            event_name: 'select_banner',
                                            banner_name: t('banner.title'),
                                            button_name: t('banner.button2'),
                                            position: 'banner_1',
                                        });
                                        signupCookie?.({
                                            sector_name: t('banner.title'),
                                            entry_position: 1,
                                        });
                                        router.push(`/${locale}/register?redirect=${encodeURIComponent(window.location.href)}`);
                                    }}
                                />
                                <JoinButton
                                    text={t('banner.button3')}
                                    theme={'light'}
                                    onClick={() => {
                                        gtm.push({
                                            event: 'uaEvent',
                                            event_name: 'select_banner',
                                            banner_name: t('banner.title'),
                                            button_name: t('banner.button3'),
                                            position: 'banner_1',
                                        });
                                        signupCookie?.({
                                            sector_name: t('banner.title'),
                                            entry_position: 1,
                                        });
                                        router.push(`/${locale}/login?redirect=${encodeURIComponent(window.location.href)}`);
                                    }}
                                />
                            </div>
                            <div>
                                <PopupLink text={t('banner.button1')} isLogin={isLogin} />
                            </div>
                        </>
                    ) : (
                        <>
                            <h1
                                className="title"
                                dangerouslySetInnerHTML={{
                                    __html: t('banner.welcome_title', { username: userInfo !== null ? getEmailPrefix(userInfo.user.email) : '' }),
                                }}
                            ></h1>
                            <h1 className="title" dangerouslySetInnerHTML={{ __html: t('banner.welcome_subtitle') }}></h1>
                            <p className="subtitle" dangerouslySetInnerHTML={{ __html: CouponTip }}></p>
                            <div className="tools-row">
                                <JoinButton
                                    text={t('banner.button2_1')}
                                    theme={'dark'}
                                    onClick={() => {
                                        gtm.push({
                                            event: 'uaEvent',
                                            event_name: 'select_banner',
                                            banner_name: t('banner.welcome_title', { username: userInfo !== null ? getEmailPrefix(userInfo.user.email) : '' }),
                                            button_name: t('banner.button2_1'),
                                            position: 'banner_1',
                                        });
                                        rewardsRef?.current && rewardsRef?.current.show();
                                    }}
                                />
                                <PopupLink text={t('banner.button1')} isLogin={isLogin} couponName={priceInfo?.discountName} />
                            </div>
                        </>
                    )}
                </div>
            </div>
            {isMounted && <RewardsModal ref={rewardsRef} />}
        </div>
    );
};

export default Banner;
